import type { OrganisationWithRole } from '@fsp/types';

export interface QuotaInfo {
  limit: number;
  isUnlimited: boolean;
  current: number;
  remaining: number;
  isAtLimit: boolean;
  isNearLimit: boolean;
  canAdd: boolean;
}

/**
 * Get team member quota info for the current organisation.
 * @param org The current organisation (from auth context)
 * @param memberCount Current number of members + pending invitations
 */
export function getTeamMemberQuotaInfo(
  org: OrganisationWithRole | null,
  memberCount: number
): QuotaInfo {
  if (!org) {
    return { limit: 0, isUnlimited: false, current: 0, remaining: 0, isAtLimit: true, isNearLimit: true, canAdd: false };
  }

  const limit = org.quotaMaxTeamMembers;
  const isUnlimited = limit === -1;
  const remaining = isUnlimited ? Infinity : Math.max(0, limit - memberCount);

  return {
    limit,
    isUnlimited,
    current: memberCount,
    remaining,
    isAtLimit: !isUnlimited && memberCount >= limit,
    isNearLimit: !isUnlimited && remaining <= 2,
    canAdd: isUnlimited || memberCount < limit,
  };
}

/**
 * Get plan quota info for the current organisation.
 * @param org The current organisation (from auth context)
 * @param planCount Current number of plans
 */
export function getPlanQuotaInfo(
  org: OrganisationWithRole | null,
  planCount: number
): QuotaInfo {
  if (!org) {
    return { limit: 0, isUnlimited: false, current: 0, remaining: 0, isAtLimit: true, isNearLimit: true, canAdd: false };
  }

  const limit = org.quotaMaxPlans;
  const isUnlimited = limit === -1;
  const remaining = isUnlimited ? Infinity : Math.max(0, limit - planCount);

  return {
    limit,
    isUnlimited,
    current: planCount,
    remaining,
    isAtLimit: !isUnlimited && planCount >= limit,
    isNearLimit: !isUnlimited && remaining <= 1,
    canAdd: isUnlimited || planCount < limit,
  };
}

/**
 * Check if AI features are enabled for the current organisation.
 * An org has AI access if quotaAiRequests is non-zero (-1 = unlimited, N > 0 = limited).
 */
export function isAiEnabled(org: OrganisationWithRole | null): boolean {
  if (!org) return false;
  return org.quotaAiRequests !== 0;
}

/**
 * Get AI request quota info for the current organisation.
 * Convention: -1 = unlimited, 0 = disabled, N = total lifetime limit.
 */
export function getAiQuotaInfo(org: OrganisationWithRole | null): QuotaInfo {
  if (!org) {
    return { limit: 0, isUnlimited: false, current: 0, remaining: 0, isAtLimit: true, isNearLimit: true, canAdd: false };
  }

  const limit = org.quotaAiRequests;
  const isUnlimited = limit === -1;
  const current = org.quotaAiRequestsUsed;
  const remaining = isUnlimited ? Infinity : Math.max(0, limit - current);

  return {
    limit,
    isUnlimited,
    current,
    remaining,
    isAtLimit: !isUnlimited && limit !== 0 && current >= limit,
    isNearLimit: !isUnlimited && limit !== 0 && remaining <= 2,
    canAdd: limit !== 0 && (isUnlimited || current < limit),
  };
}

/**
 * Format a quota limit value for display.
 * -1 becomes "Unlimited", numbers are shown as-is.
 */
export function formatQuotaValue(value: number | string | undefined): string {
  if (value === undefined || value === null) return '0';
  const num = typeof value === 'string' ? parseInt(value, 10) : value;
  if (isNaN(num)) return '0';
  return num === -1 ? 'Unlimited' : String(num);
}
